// Copyright (c) StrangeBee 2023

import { useCallback, useEffect, useState, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { CommentOutput } from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { useListAllUsers } from '@Queries/userQueries';
import { findUser } from '@Helpers/findUser';
import { useLocalMessage } from '@Helpers/locale';
import { MessageProps, Comments } from '@Molecules/Comments/Comments';

interface CommentsListProps {
    data: CommentOutput[];
    onLoadPage?: (n: number) => void;
    showLoad?: boolean;
    onEdit?: (txt: string, id: string) => void;
    onDelete?: (id: string) => void;
    readOnly?: boolean;
    currentUserLogin?: string;
    showExtraDataLinks?: 'alert' | 'case';
}

export const CommentsList = ({
    data,
    onLoadPage,
    onEdit,
    onDelete,
    showLoad = true,
    readOnly = true,
    currentUserLogin = '',
    showExtraDataLinks,
}: CommentsListProps): ReactElement => {
    const local = useLocalMessage();
    const [page, setPage] = useState(0);
    const [dataTreat, setDataTreat] = useState<MessageProps[]>([]);

    const { data: userList = [] } = useListAllUsers();

    const addMessage = useCallback(
        (newData: CommentOutput[], oldData = [] as MessageProps[]): MessageProps[] => {
            return newData.reduce((acc, current: CommentOutput): MessageProps[] => {
                const { name = '', avatar = '' } = findUser(current.createdBy, userList) || {};

                const currentTreat = {
                    objectId: current._id,
                    user: name,
                    date: current.createdAt,
                    message: current.message,
                    avatar,
                    isMine: current.createdBy === currentUserLogin,
                    readOnly,
                    extraData: current.extraData,
                };
                // Build Comment in function of showExtraDataLinks prop
                let commentPrefix = undefined;
                if (
                    showExtraDataLinks &&
                    current.extraData &&
                    current.extraData?.links &&
                    current.extraData?.links[showExtraDataLinks]
                ) {
                    if (current.extraData?.links[showExtraDataLinks]) {
                        switch (showExtraDataLinks) {
                            case 'alert': {
                                const itemId = current.extraData?.links?.alert && current.extraData?.links?.alert._id;
                                if (itemId) {
                                    commentPrefix = (
                                        <Link to={RouteBuilder.compute(`alerts.details`, { alertId: itemId })}>
                                            {local(`comment.link.alert`, { itemId })}
                                        </Link>
                                    );
                                }
                                break;
                            }

                            case 'case': {
                                const itemId = current.extraData?.links?.case && current.extraData?.links?.case.number;
                                if (itemId) {
                                    commentPrefix = (
                                        <Link to={RouteBuilder.compute(`cases.details`, { caseId: itemId })}>
                                            {local(`comment.link.case`, { itemId: `#${itemId}` })}
                                        </Link>
                                    );
                                }
                                break;
                            }
                            default:
                                break;
                        }
                    }
                }

                const option = current.updatedAt ? { edited: current.updatedAt } : {};

                return [{ ...currentTreat, ...option, prefix: commentPrefix }, ...acc];
            }, oldData as MessageProps[]);
        },
        [userList, currentUserLogin, readOnly, showExtraDataLinks, local],
    );

    useEffect(() => {
        setDataTreat(addMessage(data, []));
    }, [data, userList, readOnly]); //eslint-disable-line

    useEffect(() => {
        onLoadPage?.(page);
    }, [page, onLoadPage]);

    return (
        <Comments
            data={dataTreat}
            onEdit={onEdit}
            onDelete={onDelete}
            moreConfig={{
                message: local('comment.actions.loadMore'),
                show: !!(showLoad && onLoadPage),
                onClick: () => {
                    setPage((p) => p + 1);
                },
            }}
        />
    );
};
